import { createSelector } from '@ngrx/store';

import { AppState } from '../root-reducer';
import { CustomerActions, CustomerActionTypes } from '../actions/customer.actions';

import { Comment } from 'modules/shared/models/comment.models';
import { Crp } from 'modules/shared/models/crp.models';
import { RiskReportComments } from 'modules/shared/models/risk-assessment.models';
import { CrpResponse } from 'store/models/crp.models';
import { DocumentResponse } from 'store/models/document.models';
import { CustomerResponse, GetCustomersBatchUploadsResponse, CustomerError } from 'store/models/customer.models';
import { AccountListResponse } from 'store/models/admin.models';
import { UserNotificationRow } from 'modules/shared/data-source/user-notifications.data-source';

export interface State {
  loading: Array<CustomerActionTypes>;
  isUploadingCustomersBatchFile: boolean;
  customer: any | null;
  customerStatus: any | null;
  record: any | null;
  customers: CustomerResponse | null;
  currentCrp: Crp | null;
  crps: CrpResponse | null;
  crpsNoPrimary: CrpResponse | null;
  documents: DocumentResponse | null;
  customersBatchUploads: GetCustomersBatchUploadsResponse | null;
  comments: RiskReportComments | null;
  customerComments: Comment | null;
  error: Array<CustomerError>;
  defaultNotifiedUsers: Array<UserNotificationRow>;
  users: AccountListResponse | null;
  entitytypes: string[] | [];
  ownershipLayers: string[] | [];
  countries: string[] | [];
  ssoc: string[] | [];
  ssic: string[] | [];
  titles: string[] | [];
  idTypes: string[] | [];
  sourceOfFunds: string[] | [];
  onboardingModes: string[] | [];
  paymentModes: string[] | [];
  productServiceComplexities: string[] | [];
}

const initialState: State = {
  loading: [],
  isUploadingCustomersBatchFile: null,
  customer: null,
  customerStatus: null,
  record: null,
  customers: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },
  currentCrp: null,
  crps: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },
  crpsNoPrimary: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },
  documents: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },
  customersBatchUploads: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },
  comments: null,
  defaultNotifiedUsers: null,
  users: {
    count: 0,
    next: null,
    previous: null,
    results: []
  },
  entitytypes: [],
  ownershipLayers: [],
  countries: [],
  ssoc: [],
  ssic: [],
  idTypes: [],
  sourceOfFunds: [],
  titles: [],
  onboardingModes: [],
  paymentModes: [],
  productServiceComplexities: [],
  customerComments: null,
  error: []
};

export function reducer(state: State = initialState, action: CustomerActions): State {
  switch (action.type) {
    case CustomerActionTypes.CREATE_DRAFT_CUSTOMER: {
      return {
        ...state,
        loading: [...state.loading, CustomerActionTypes.CREATE_OR_UPDATE_CUSTOMER]
      };
    }
    case CustomerActionTypes.CREATE_AND_RA_CUSTOMER: {
      return {
        ...state,
        loading: [...state.loading, CustomerActionTypes.CREATE_OR_UPDATE_CUSTOMER]
      };
    }
    case CustomerActionTypes.CREATE_AND_CRP_CUSTOMER: {
      return {
        ...state,
        loading: [...state.loading, CustomerActionTypes.CREATE_OR_UPDATE_CUSTOMER]
      };
    }

    case CustomerActionTypes.CREATE_OR_UPDATE_CUSTOMER: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }
    case CustomerActionTypes.CREATE_OR_UPDATE_CUSTOMER_SUCCESS: {
      return {
        ...state,
        error: state.error.filter((item: CustomerError) =>
          item.action !== CustomerActionTypes.CREATE_OR_UPDATE_CUSTOMER),
        loading: clearLoading(state, CustomerActionTypes.CREATE_OR_UPDATE_CUSTOMER)
      };
    }
    case CustomerActionTypes.CREATE_OR_UPDATE_CUSTOMER_FAILURE: {
      return {
        ...state,
        error: [...state.error, <CustomerError>{
          action: CustomerActionTypes.CREATE_OR_UPDATE_CUSTOMER,
          detail: action.payload
        }],
        loading: clearLoading(state, CustomerActionTypes.CREATE_OR_UPDATE_CUSTOMER)
      };
    }
    case CustomerActionTypes.CREATE_AND_ADD_CRP: {
      return {
        ...state,
        loading: [...state.loading, CustomerActionTypes.CREATE_OR_UPDATE_CRP]
      };
    }
    case CustomerActionTypes.CREATE_AND_RA_CRP: {
      return {
        ...state,
        loading: [...state.loading, CustomerActionTypes.CREATE_OR_UPDATE_CRP]
      };
    }
    case CustomerActionTypes.UPDATE_AND_RA_CRP: {
      return {
        ...state,
        loading: [...state.loading, CustomerActionTypes.CREATE_OR_UPDATE_CRP]
      };
    }
    case CustomerActionTypes.UPDATE_AND_ADD_CRP: {
      return {
        ...state,
        loading: [...state.loading, CustomerActionTypes.CREATE_OR_UPDATE_CRP]
      };
    }

    case CustomerActionTypes.CREATE_OR_UPDATE_CRP: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }
    case CustomerActionTypes.CREATE_OR_UPDATE_CRP_SUCCESS: {
      return {
        ...state,
        error: state.error.filter((item: CustomerError) =>
          item.action !== CustomerActionTypes.CREATE_OR_UPDATE_CRP),
        loading: clearLoading(state, CustomerActionTypes.CREATE_OR_UPDATE_CRP)
      };
    }
    case CustomerActionTypes.CREATE_OR_UPDATE_CRP_FAILURE: {
      return {
        ...state,
        error: [...state.error, <CustomerError>{
          action: CustomerActionTypes.CREATE_OR_UPDATE_CRP,
          detail: action.payload
        }],
        loading: clearLoading(state, CustomerActionTypes.CREATE_OR_UPDATE_CRP)
      };
    }

    case CustomerActionTypes.GET_CUSTOMER: {
      return {
        ...state,
        loading: action.payload.quietUpdate ? state.loading : [...state.loading, action.type]
      };
    }
    case CustomerActionTypes.GET_CUSTOMER_SUCCESS: {
      return {
        ...state,
        customer: action.payload,
        error: state.error.filter((item: CustomerError) =>
          item.action !== CustomerActionTypes.GET_CUSTOMER),
        loading: clearLoading(state, CustomerActionTypes.GET_CUSTOMER)
      };
    }
    case CustomerActionTypes.GET_CUSTOMER_FAILURE: {
      return {
        ...state,
        customer: action.payload,
        error: [...state.error, <CustomerError>{
          action: CustomerActionTypes.GET_CUSTOMER,
          detail: action.payload
        }],
        loading: clearLoading(state, CustomerActionTypes.GET_CUSTOMER)
      };
    }

    case CustomerActionTypes.GET_CUSTOMER_STATUS: {
      return {
        ...state
      };
    }
    case CustomerActionTypes.GET_CUSTOMER_STATUS_SUCCESS: {
      return {
        ...state,
        customerStatus: action.payload,
        error: state.error.filter((item: CustomerError) =>
          item.action !== CustomerActionTypes.GET_CUSTOMER_STATUS),
      };
    }
    case CustomerActionTypes.GET_CUSTOMER_STATUS_FAILURE: {
      return {
        ...state,
        error: [...state.error, <CustomerError>{
          action: CustomerActionTypes.GET_CUSTOMER_STATUS,
          detail: action.payload
        }]
      };
    }

    case CustomerActionTypes.RESET_CUSTOMER_OVERVIEW: {
      return {
        ...state,
        customer: action.payload,
        currentCrp: null,
        crps: {
          count: 0,
          next: null,
          previous: null,
          results: []
        },
        crpsNoPrimary: {
          count: 0,
          next: null,
          previous: null,
          results: []
        }
      };
    }

    case CustomerActionTypes.GET_CUSTOMERS: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }
    case CustomerActionTypes.GET_CUSTOMERS_SUCCESS: {
      return {
        ...state,
        customers: action.payload,
        error: state.error.filter((item: CustomerError) =>
          item.action !== CustomerActionTypes.GET_CUSTOMERS),
        loading: clearLoading(state, CustomerActionTypes.GET_CUSTOMERS)
      };
    }
    case CustomerActionTypes.GET_CUSTOMERS_FAILURE: {
      return {
        ...state,
        error: [...state.error, <CustomerError>{
          action: CustomerActionTypes.GET_CUSTOMERS,
          detail: action.payload
        }],
        loading: clearLoading(state, CustomerActionTypes.GET_CUSTOMERS)
      };
    }

    case CustomerActionTypes.GET_CRP: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }
    case CustomerActionTypes.GET_CRP_SUCCESS: {
      return {
        ...state,
        currentCrp: action.payload,
        error: state.error.filter((item: CustomerError) =>
          item.action !== CustomerActionTypes.GET_CRP),
        loading: clearLoading(state, CustomerActionTypes.GET_CRP)
      };
    }
    case CustomerActionTypes.GET_CRP_FAILURE: {
      return {
        ...state,
        currentCrp: null,
        error: [...state.error, <CustomerError>{
          action: CustomerActionTypes.GET_CRP,
          detail: action.payload
        }],
        loading: clearLoading(state, CustomerActionTypes.GET_CRP)
      };
    }

    case CustomerActionTypes.UPDATE_CRP_SCREENING: {
      const index = state.crps.results.findIndex((crp: Crp) => crp.id === action.payload.crp);
      return {
        ...state,
        currentCrp: {
          ...state.currentCrp,
          latestScreeningConclusion: action.payload
        },
        crps: {
          ...state.crps,
          results: (index > -1) ? [
            ...state.crps.results.slice(0, index),
            Object.assign({}, state.crps.results[index], {
              ...state.crps.results[index],
              latestScreeningConclusion: action.payload
            }),
            ...state.crps.results.slice(index + 1)
          ] : state.crps.results
        }
      };
    }

    case CustomerActionTypes.GET_CRPS_NO_PRIMARY:
    case CustomerActionTypes.GET_CRPS: {
      return {
        ...state,
        loading: action.payload.quietUpdate ? state.loading : [...state.loading, action.type]
      };
    }

    case CustomerActionTypes.GET_CRPS_SUCCESS: {
      return {
        ...state,
        crps: action.payload,
        error: state.error.filter((item: CustomerError) =>
          item.action !== CustomerActionTypes.GET_CRPS),
        loading: clearLoading(state, CustomerActionTypes.GET_CRPS)
      };
    }
    case CustomerActionTypes.GET_CRPS_FAILURE: {
      return {
        ...state,
        error: [...state.error, <CustomerError>{
          action: CustomerActionTypes.GET_CRPS,
          detail: action.payload
        }],
        loading: clearLoading(state, CustomerActionTypes.GET_CRPS)
      };
    }
    case CustomerActionTypes.GET_CRPS_NO_PRIMARY_SUCCESS: {
      return {
        ...state,
        crpsNoPrimary: action.payload,
        error: state.error.filter((item: CustomerError) =>
          item.action !== CustomerActionTypes.GET_CRPS_NO_PRIMARY),
        loading: clearLoading(state, CustomerActionTypes.GET_CRPS_NO_PRIMARY)
      };
    }
    case CustomerActionTypes.GET_CRPS_NO_PRIMARY_FAILURE: {
      return {
        ...state,
        error: [...state.error, <CustomerError>{
          action: CustomerActionTypes.GET_CRPS_NO_PRIMARY,
          detail: action.payload
        }],
        loading: clearLoading(state, CustomerActionTypes.GET_CRPS_NO_PRIMARY)
      };
    }

    case CustomerActionTypes.GET_CUSTOMER_DOCUMENTS: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }
    case CustomerActionTypes.GET_CUSTOMER_DOCUMENTS_SUCCESS: {
      return {
        ...state,
        documents: action.payload,
        error: state.error.filter((item: CustomerError) =>
          item.action !== CustomerActionTypes.GET_CUSTOMER_DOCUMENTS),
        loading: clearLoading(state, CustomerActionTypes.GET_CUSTOMER_DOCUMENTS)
      };
    }
    case CustomerActionTypes.GET_CUSTOMER_DOCUMENTS_FAILURE: {
      return {
        ...state,
        error: [...state.error, <CustomerError>{
          action: CustomerActionTypes.GET_CUSTOMER_DOCUMENTS,
          detail: action.payload
        }],
        documents: {
          count: 0,
          next: null,
          previous: null,
          results: []
        },
        loading: clearLoading(state, CustomerActionTypes.GET_CUSTOMER_DOCUMENTS)
      };
    }

    case CustomerActionTypes.GET_CUSTOMERS_BATCH_UPLOADS_LIST: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }
    case CustomerActionTypes.GET_CUSTOMERS_BATCH_UPLOADS_LIST_SUCCESS: {
      return {
        ...state,
        customersBatchUploads: action.payload,
        error: state.error.filter((item: CustomerError) =>
          item.action !== CustomerActionTypes.GET_CUSTOMERS_BATCH_UPLOADS_LIST),
        loading: clearLoading(state, CustomerActionTypes.GET_CUSTOMERS_BATCH_UPLOADS_LIST)
      };
    }
    case CustomerActionTypes.GET_CUSTOMERS_BATCH_UPLOADS_LIST_FAILURE: {
      return {
        ...state,
        error: [...state.error, <CustomerError>{
          action: CustomerActionTypes.GET_CUSTOMERS_BATCH_UPLOADS_LIST,
          detail: action.payload
        }],
        customersBatchUploads: {
          count: 0,
          next: null,
          previous: null,
          results: []
        },
        loading: clearLoading(state, CustomerActionTypes.GET_CUSTOMERS_BATCH_UPLOADS_LIST)
      };
    }

    case CustomerActionTypes.UPLOAD_CUSTOMERS_BATCH_FILE: {
      return {
        ...state,
        isUploadingCustomersBatchFile: true
      };
    }
    case CustomerActionTypes.UPLOAD_CUSTOMERS_BATCH_FILE_SUCCESS: {
      return {
        ...state,
        isUploadingCustomersBatchFile: false,
        error: state.error.filter((item: CustomerError) =>
          item.action !== CustomerActionTypes.UPLOAD_CUSTOMERS_BATCH_FILE)
      };
    }
    case CustomerActionTypes.UPLOAD_CUSTOMERS_BATCH_FILE_FAILURE: {
      return {
        ...state,
        error: [...state.error, <CustomerError>{
          action: CustomerActionTypes.UPLOAD_CUSTOMERS_BATCH_FILE,
          detail: action.payload
        }],
        isUploadingCustomersBatchFile: false
      };
    }

    case CustomerActionTypes.CLEAR_GET_CUSTOMERS_BATCH_UPLOADS_LIST: {
      return {
        ...state,
        customersBatchUploads: {
          count: 0,
          next: null,
          previous: null,
          results: []
        },
        loading: clearLoading(state, CustomerActionTypes.GET_CUSTOMERS_BATCH_UPLOADS_LIST)
      };
    }

    case CustomerActionTypes.GET_USERS: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }
    case CustomerActionTypes.GET_USERS_SUCCESS: {
      return {
        ...state,
        users: action.payload,
        error: state.error.filter((item: CustomerError) =>
          item.action !== CustomerActionTypes.GET_USERS),
        loading: clearLoading(state, CustomerActionTypes.GET_USERS)
      };
    }
    case CustomerActionTypes.GET_USERS_FAILURE: {
      return {
        ...state,
        error: [...state.error, <CustomerError>{
          action: CustomerActionTypes.GET_USERS,
          detail: action.payload
        }],
        loading: clearLoading(state, CustomerActionTypes.GET_USERS)
      };
    }

    case CustomerActionTypes.SET_DEFAULT_NOTIFIED_USERS: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }
    case CustomerActionTypes.SET_DEFAULT_NOTIFIED_USERS_SUCCESS: {
      return {
        ...state,
        loading: clearLoading(state, CustomerActionTypes.SET_DEFAULT_NOTIFIED_USERS),
        defaultNotifiedUsers: action.payload,
        error: state.error.filter((item: CustomerError) =>
          item.action !== CustomerActionTypes.SET_DEFAULT_NOTIFIED_USERS)
      };
    }
    case CustomerActionTypes.SET_DEFAULT_NOTIFIED_USERS_FAILURE: {
      return {
        ...state,
        loading: clearLoading(state, CustomerActionTypes.SET_DEFAULT_NOTIFIED_USERS),
        error: [...state.error, <CustomerError>{
          action: CustomerActionTypes.SET_DEFAULT_NOTIFIED_USERS,
          detail: action.payload
        }]
      };
    }

    case CustomerActionTypes.GET_DEFAULT_NOTIFIED_USERS: {
      return {
        ...state,
        loading: [...state.loading, action.type]
      };
    }
    case CustomerActionTypes.GET_DEFAULT_NOTIFIED_USERS_SUCCESS: {
      return {
        ...state,
        loading: clearLoading(state, CustomerActionTypes.GET_DEFAULT_NOTIFIED_USERS),
        defaultNotifiedUsers: action.payload,
        error: state.error.filter((item: CustomerError) =>
          item.action !== CustomerActionTypes.GET_DEFAULT_NOTIFIED_USERS)
      };
    }
    case CustomerActionTypes.GET_DEFAULT_NOTIFIED_USERS_FAILURE: {
      return {
        ...state,
        loading: clearLoading(state, CustomerActionTypes.GET_DEFAULT_NOTIFIED_USERS),
        error: [...state.error, <CustomerError>{
          action: CustomerActionTypes.GET_DEFAULT_NOTIFIED_USERS,
          detail: action.payload
        }]
      };
    }

    case CustomerActionTypes.PATCH_CUSTOMER: {
      return {
        ...state,
        loading: action.payload.quietUpdate ? state.loading : [...state.loading, action.type]
      };
    }
    case CustomerActionTypes.PATCH_CUSTOMER_SUCCESS: {
      return {
        ...state,
        customer: action.payload,
        error: state.error.filter((item: CustomerError) =>
          item.action !== CustomerActionTypes.PATCH_CUSTOMER),
        loading: clearLoading(state, CustomerActionTypes.PATCH_CUSTOMER)
      };
    }
    case CustomerActionTypes.PATCH_CUSTOMER_FAILURE: {
      return {
        ...state,
        error: [...state.error, <CustomerError>{
          action: CustomerActionTypes.PATCH_CUSTOMER,
          detail: action.payload
        }],
        loading: clearLoading(state, CustomerActionTypes.PATCH_CUSTOMER)
      };
    }

    case CustomerActionTypes.CLEAR_ERRORS: {
      return {
        ...state,
        error: [],
      };
    }

    default: {
      return state;
    }
  }
}

const customer = (state: AppState) => state.customer;
export const Selectors = {
  getErrors: createSelector(customer, (state: State) => state.error),
  getLoading: createSelector(customer, (state: State) => state.loading),
  isUploadingCustomersBatchFile: createSelector(customer, (state: State) => state.isUploadingCustomersBatchFile),
  getCustomer: createSelector(customer, (state: State) => state.customer),
  getCustomerStatus: createSelector(customer, (state: State) => state.customerStatus),
  getCustomers: createSelector(customer, (state: State) => state.customers),
  getCurrentCrp: createSelector(customer, (state: State) => state.currentCrp),
  getCrps: createSelector(customer, (state: State) => state.crps),
  getCrpsNoPrimary: createSelector(customer, (state: State) => state.crpsNoPrimary),
  getDocuments: createSelector(customer, (state: State) => state.documents),
  getCustomersBatchUploads: createSelector(customer, (state: State) => state.customersBatchUploads),
  getComments: createSelector(customer, (state: State) => state.comments),
  getUsers: createSelector(customer, (state: State) => state.users),
  getDefaultNotifiedUsers: createSelector(customer, (state: State) => state.defaultNotifiedUsers)
};

function clearLoading(authState: State, action: CustomerActionTypes): Array<CustomerActionTypes> {
  return authState.loading.filter((loadingMarker: CustomerActionTypes) => loadingMarker !== action);
}
